/**
 * Session-level UI (10 §2/§3/§6, 04 §10): queue state line, vs BOT, REMATCH,
 * pause/resume overlay, reconnect/replaced notices, idle warning. DOM
 * overlay in the HUD register; the canvas HUD stays game-only.
 */
import { SIM_HZ } from '../../sim/constants.js'
import type { DisclordView, LinkCodeMsg, KioskMsg, RunStateMsg, MatchResultMsg, PartyStateMsg, PauseMsg, QueueStateMsg } from '../../shared/protocol.js'

export interface SessionActions {
  /** D11 vs BOT; tier defaults to PRO (spec), ROOKIE is the learn-the-lead option (2026-09-13 playtest). */
  vsBot: (tier?: 'ROOKIE' | 'PRO') => void
  rematch: (want: boolean) => void
  leaveToHub: () => void
}

export class SessionUI {
  queue: QueueStateMsg = { state: 'OFF', flagged: 0, launchInMs: null, vsBotAvailable: false, vsBotInMs: null }
  kiosk: KioskMsg | null = null
  /** 10 §2 party (Sprint 7). */
  party: PartyStateMsg = { code: null, members: [] }
  /** 12 §1 disclord (Sprint 8). */
  disclord: DisclordView | null = null
  /** 12 §7.4 last issued link code (Sprint 8). */
  linkCode: LinkCodeMsg | null = null
  /** 12 §4 run in progress (Sprint 9); cleared on the next hub join. */
  run: RunStateMsg | null = null
  /** 13 §4 playtest knobs in effect on this server (from the welcome/resync state); labels the session. */
  knobs: Record<string, number | boolean> | null = null
  pause: (PauseMsg & { at: number }) | null = null
  resumeAt: number | null = null
  result: MatchResultMsg | null = null
  notice: { text: string; until: number } | null = null
  status: 'connected' | 'reconnecting' | 'replaced' = 'connected'
  rematchWanted = false
  private readonly el: HTMLElement

  constructor(root: HTMLElement, private readonly actions: SessionActions) {
    this.el = root
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement
      const act = t.dataset.act
      if (act === 'vsbot') this.actions.vsBot(t.dataset.tier === 'ROOKIE' ? 'ROOKIE' : 'PRO')
      else if (act === 'rematch') { this.rematchWanted = !this.rematchWanted; this.actions.rematch(this.rematchWanted) }
      else if (act === 'hub') this.actions.leaveToHub()
    })
  }

  setQueue(q: QueueStateMsg): void { this.queue = q }
  setPause(p: PauseMsg | null, now: number): void { this.pause = p ? { ...p, at: now } : null; if (p) this.resumeAt = null }
  setResume(inTicks: number, now: number): void { this.pause = null; this.resumeAt = now + (inTicks / SIM_HZ) * 1000 }
  setResult(r: MatchResultMsg | null): void { this.result = r; if (!r) this.rematchWanted = false }
  say(text: string, now: number, ms = 4000): void { this.notice = { text, until: now + ms } }

  render(now: number, inHub: boolean, roomPhase: string): void {
    const lines: string[] = []
    if (this.status === 'reconnecting') lines.push('<div class="ses-big">RECONNECTING…</div>')
    if (this.status === 'replaced') lines.push('<div class="ses-big">SIGNED IN ELSEWHERE — <span data-act="hub">[ENTER] RECONNECT HERE</span></div>')
    // Z2/Z10/Z11/Z12 and the results screen are canvas HUD (09); this overlay keeps only
    // what needs a DOM button: the D11 "vs BOT" offer and session notices.
    if (inHub && this.queue.state === 'FLAGGED' && this.queue.vsBotAvailable) lines.push('<div><button data-act="vsbot" data-tier="PRO">vs BOT · PRO [B]</button> <button data-act="vsbot" data-tier="ROOKIE">vs BOT · ROOKIE (learn the lead)</button></div>')
    // 12 §4 run rail / results (Sprint 9).
    const r = this.run
    if (r && (r.stage === 'BOUT' || r.stage === 'WARDEN')) lines.push(`<div class="ses-dim">RUN · ${r.stage === 'WARDEN' ? `WARDEN W${r.tier}` : `BOUT ${r.bout}/3`} · ${mmss(r.timeLeftMs)} left${r.cards.length ? ` · cards ${r.cards.join(', ')}` : ''}</div>`)
    if (r && r.stage === 'RESULTS') lines.push(`<div class="ses-big">RUN ${r.outcome === 'won' ? 'CLEARED' : r.outcome === 'failed' ? 'FAILED' : 'ABANDONED'}</div><div>${r.xp ? `+${r.xp} XP` : 'no XP'}${r.firstClear ? ' · +1 IMPRINT TOKEN · first clear' : ''}</div>`)
    if (this.knobs) lines.push(`<div class="ses-dim">PLAYTEST KNOBS · ${esc(Object.entries(this.knobs).map(([k, v]) => `${k} ${v}`).join(' · '))}</div>`)
    void roomPhase
    if (this.notice && now < this.notice.until) lines.push(`<div class="ses-notice">${esc(this.notice.text)}</div>`)
    const html = lines.join('')
    if (html !== this.el.innerHTML) this.el.innerHTML = html
    this.el.style.display = html ? '' : 'none'
  }
}

const mmss = (ms: number): string => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
const esc = (s: string): string => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!)
